import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {SelectPanel, type SelectPanelItemInput} from '@primer/react'
import {useReviewers} from '../../data/use-reviewers'
import type {Reviewer} from '../../../../page-data/payloads/review-requests'
import {buildGroupMetadata, buildReviewerOptions, mergeCurrentReviewers} from './reviewer-options'
import {useReviewersDebounced} from './use-reviewers-debounced'
import {useReviewerSelectionSession} from './use-reviewer-selection-session'
import {toSelectPanelItem} from './to-panel-item'

export interface ReviewerPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  anchorRef: React.RefObject<HTMLButtonElement | null>
}

// Stable empty default so an absent typeahead response doesn't churn memo identity.
const EMPTY_REVIEWERS: Reviewer[] = []

export function ReviewerPicker(props: ReviewerPickerProps) {
  const {open} = props
  if (!open) return null

  return (
    <ErrorBoundary boundaryName="ReviewerPicker" fallback={null}>
      <Suspense fallback={null}>
        <ReviewerPickerContent {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

function ReviewerPickerContent({open, onOpenChange, anchorRef}: ReviewerPickerProps) {
  const {data: reviewers} = useReviewers()
  const {filter, onFilterChange, searchedReviewers, isFetching} = useReviewersDebounced()

  const currentReviewers = mergeCurrentReviewers(reviewers)
  const currentLogins = new Set(currentReviewers.map(r => r.displayLogin))

  const options = buildReviewerOptions({
    suggestedReviewers: reviewers.suggestedReviewers,
    searchedReviewers: searchedReviewers ?? EMPTY_REVIEWERS,
    currentReviewers,
    currentLogins,
    filter,
  })
  const groupMetadata = buildGroupMetadata(options)

  const {onSelectionChange, draftLogins} = useReviewerSelectionSession({options, currentLogins})
  const items = options.map(toSelectPanelItem)
  const selected = items.filter(item => item.id != null && draftLogins.has(String(item.id)))

  // Bridge SelectPanel's item-based selection back to logins, reconciling only
  // the visible items so selections hidden by the active filter are preserved.
  const handleSelectedChange = (next: SelectPanelItemInput | SelectPanelItemInput[] | undefined) => {
    const nextList = Array.isArray(next) ? next : next ? [next] : []
    const nextLogins = new Set<string>()
    for (const item of nextList) {
      if (item.id != null) nextLogins.add(String(item.id))
    }
    const visibleLogins = items.filter(item => item.id != null).map(item => String(item.id))
    onSelectionChange(visibleLogins, nextLogins)
  }

  return (
    <SelectPanel
      title="Request up to 100 reviewers"
      open={open}
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      renderAnchor={null}
      // Align the panel's right edge with the gear button so it extends
      // left into the sidebar instead of overflowing rightward.
      align="end"
      items={items}
      groupMetadata={groupMetadata}
      selected={selected}
      onSelectedChange={handleSelectedChange}
      filterValue={filter}
      onFilterChange={onFilterChange}
      placeholderText="Type or choose a user"
      inputLabel="Filter reviewers"
      loading={isFetching}
      initialLoadingType="spinner"
      message={
        items.length === 0 && !isFetching
          ? {
              variant: 'empty',
              title: 'No reviewers available',
              body: filter ? 'No reviewers match your search.' : 'No reviewers are available for this pull request.',
            }
          : undefined
      }
      overlayProps={{
        width: 'medium',
        maxHeight: 'large',
      }}
    />
  )
}
